/**
 * TURNING A PATTERN INTO REAL DATES
 * ----------------------------------
 * "Every Wednesday and Saturday at 5pm until the end of November" has to
 * become a list of actual moments before it can be sent anywhere.
 *
 * THIS HAPPENS IN THE BROWSER ON PURPOSE, and it is the one place on the site
 * where a real decision is made outside the backend. The reason is time zones.
 *
 * "5pm" means five in the afternoon where the poster is standing. The server
 * runs in UTC, in a data centre in Nuremberg, and has no reliable way of
 * knowing what the poster meant - so it would have to guess, and it would get
 * it wrong by an hour twice a year when the clocks change. Being an hour out
 * is not a cosmetic bug here: it is a sixteen-year-old at somebody's door at
 * the wrong time.
 *
 * `new Date(year, month, day, hours, minutes)` uses the browser's own clock,
 * so the moment that comes out is the one the poster meant, and `.toISOString()`
 * hands the server something unambiguous. It is exactly what the single-date
 * `datetime-local` input on this form has always done.
 *
 * The server still checks everything that matters about the result - how many,
 * how far ahead, in the future, no duplicates, no overlaps - in
 * backend/utils/visits.js. This file decides the dates; it does not get to
 * decide whether they are allowed.
 */

/**
 * Every half hour of the day, as plain 24-hour text.
 *
 * The same list, and the same reason, as the one that used to be written out
 * inside MyOffer.jsx: `<input type="time">` is drawn by the BROWSER in
 * whatever format the machine's locale says, which on an English setting means
 * "5:00 PM". This is an Italian school and the clock has 24 hours on it.
 *
 * It lives here now because the posting form needs it too, and two copies of a
 * list like this drift the moment somebody changes the step to 15 minutes in
 * one of them.
 */
export const HALF_HOURS = [];
for (let h = 0; h < 24; h += 1) {
  HALF_HOURS.push(`${String(h).padStart(2, '0')}:00`);
  HALF_HOURS.push(`${String(h).padStart(2, '0')}:30`);
}

// The active locale lives in format.js, pushed there by I18nProvider - see the
// note at the top of that file. Imported rather than duplicated so a date
// written out by this file and one written out by format.js can never end up
// in two different languages on the same screen.
import { currentLocale, phrase } from './format';

/**
 * The time stored for a chore whose poster does not mind when.
 *
 * A flexible chore still holds a real moment - the 24-hour cancellation rule,
 * the clash check against a student's other work and the ordering of the
 * marketplace all read one. This is what goes in, and the screens print
 * "flexible" instead of showing it.
 *
 * Nine in the morning rather than midnight because the stored time is what
 * the cancellation window counts back from, and it should be the earliest
 * hour somebody might really be asked to turn up. See models/Chore.js.
 */
export const FLEXIBLE_TIME = '09:00';

/** '2026-08-20' -> a real Date at the given time, in the viewer's own zone. */
export function atTime(key, time) {
  const [y, m, d] = key.split('-').map(Number);
  const [hh, mm] = String(time || '10:00').split(':').map(Number);
  return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
}

/**
 * The chosen days, as visits the API will accept.
 *
 * Sorted, because the poster taps them in whatever order they think of them -
 * "Friday, oh and Monday" - and both the summary they read back and the
 * server's duplicate check want them in order.
 */
export function visitsFromDays(dayKeys, time, hours) {
  const length = (Number(hours) || 1) * 3600000;

  return [...dayKeys]
    .sort()
    .map((key) => {
      const start = atTime(key, time);
      return { start: start.toISOString(), end: new Date(start.getTime() + length).toISOString() };
    });
}

/**
 * Every date matching a weekly pattern, between today and a closing date.
 *
 * @param weekdays  0 for Sunday through 6 for Saturday, matching getDay()
 * @param until     the last day that may be included
 * @param maxVisits stop here even if the pattern would go on - the server
 *                  refuses beyond this, and running past it in the browser
 *                  produces a list that can only be rejected
 */
export function daysFromWeekly(weekdays, until, maxVisits = 100) {
  if (!weekdays || weekdays.length === 0 || !until) return [];

  const wanted = new Set(weekdays.map(Number));
  const out = [];

  // From tomorrow. Today is excluded because a pattern starting "today" would
  // include a time that may already have passed, and the server would reject
  // the whole run over one date the poster did not think they had chosen.
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  cursor.setDate(cursor.getDate() + 1);

  const last = new Date(until);
  last.setHours(23, 59, 59, 999);

  while (cursor <= last && out.length < maxVisits) {
    if (wanted.has(cursor.getDay())) {
      const pad = (n) => String(n).padStart(2, '0');
      out.push(`${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(cursor.getDate())}`);
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return out;
}

/** The furthest day that may be booked, for capping the calendar. */
export function horizonDate(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + (Number(months) || 3));
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * A date written out in full: "Saturday 14 November 2026".
 *
 * Shown under the two fields the BROWSER draws - `type="date"` and
 * `type="datetime-local"`. Those controls pick their own format from the
 * page's language, and on a browser set to US English an unqualified "en"
 * paints 11/14/2026. The page now says `lang="en-GB"` (see index.html), which
 * is the actual fix, but that puts the whole thing in the hands of one
 * attribute being honoured.
 *
 * So the date is also written out in words next to the box. Spelling out the
 * month cannot be read the wrong way round by anybody, whatever the control
 * decided to paint, and 11/14 versus 14/11 is not a cosmetic disagreement -
 * it is a student turning up three months early.
 */
export function longDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(currentLocale(), {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

/**
 * The same, plus the time: "Saturday 14 November 2026 at 10:00".
 *
 * The joining word comes from the phrase file. It used to be the English
 * word "at" written straight into this line, so an Italian reader got
 * "sabato 14 novembre 2026 at 10:00" - the one English word in an otherwise
 * translated sentence, on the field that tells somebody when to turn up.
 * `npm run check-untranslated` had been reporting it as "... at ...)" for
 * weeks; it is the kind of thing that list is for.
 */
export function longDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const clock = d.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' });
  return phrase('time.dateAtTime', { date: longDate(value), time: clock });
}

/**
 * When a chore happens, as a person would say it.
 *
 * THE ONE PLACE that decides how a chore's "when" is written, so that a
 * flexible one cannot end up saying "flexible" on the marketplace and "09:00"
 * on the dashboard. Every screen showing a chore's time asks this.
 */
export function choreWhen(chore) {
  if (!chore?.scheduledFor) return '';
  if (chore.timeFlexible) {
    return phrase('time.dateFlexible', { date: longDate(chore.scheduledFor) });
  }
  return longDateTime(chore.scheduledFor);
}

/** The short version, for a card: "Sat 14 Nov, flexible" or "Sat 14 Nov, 10:00". */
export function choreWhenShort(chore) {
  if (!chore?.scheduledFor) return '';
  const d = new Date(chore.scheduledFor);
  const day = d.toLocaleDateString(currentLocale(), {
    weekday: 'short', day: 'numeric', month: 'short',
  });
  if (chore.timeFlexible) return `${day}, ${phrase('time.flexible')}`;
  return `${day}, ${d.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * "Monday 25 August, 17:00" - one visit, written out for a human.
 *
 * `flexible` is passed by the caller rather than read off the visit, because
 * it is a property of the whole chore: "Wednesdays, whenever suits" is a
 * sentence somebody says, and "Wednesday at four but Saturday whenever" is
 * not. See models/Chore.js.
 */
export function describeVisit(visit, flexible = false) {
  const start = new Date(visit.start);
  const day = start.toLocaleDateString(currentLocale(), {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  if (flexible) return `${day}, ${phrase('time.flexible')}`;
  return `${day}, ${start.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' })}`;
}
